import React from 'react'
import { Table, TableColumnsType } from 'antd'
import { IMovementRS } from '../../../models/salesModel'
import { textNumberFormater } from '../../../utils'
import { groupSalesByPos, calculateSummary, TGroupedData } from '../../../utils/reportUtils'
import { TExcelColumn } from '../../../utils/excelExport'
import ReportSummaryCards, { TSummaryCard } from './ReportSummaryCards'
import {
  DollarOutlined,
  TrophyOutlined,
  ShopOutlined,
  RiseOutlined
} from '@ant-design/icons'

type SalesByPosReportProps = {
  data: IMovementRS[]
  loading?: boolean
}

function SalesByPosReport({ data, loading }: Readonly<SalesByPosReportProps>) {
  const grouped = groupSalesByPos(data)
  const summary = calculateSummary(data)
  const topPos = grouped[0]

  const cards: TSummaryCard[] = [
    {
      title: 'Total Vendido',
      value: summary.totalAmount,
      format: 'money',
      color: '#52c41a',
      prefix: <DollarOutlined />
    },
    {
      title: 'Puntos de Venta',
      value: grouped.length,
      format: 'number',
      color: '#1890ff',
      prefix: <ShopOutlined />
    },
    {
      title: 'POS Top',
      value: topPos?.label || '-',
      format: 'text',
      color: '#faad14',
      prefix: <TrophyOutlined />
    },
    {
      title: 'Promedio por POS',
      value: grouped.length > 0 ? Math.round(summary.totalAmount / grouped.length) : 0,
      format: 'money',
      color: '#722ed1',
      prefix: <RiseOutlined />
    }
  ]

  const columns: TableColumnsType = [
    { title: '#', dataIndex: 'rank', width: 50 },
    { title: 'Punto de Venta', dataIndex: 'label', width: 220 },
    {
      title: 'Facturas',
      dataIndex: 'count',
      width: 100,
      align: 'center',
      sorter: (a: any, b: any) => a.count - b.count
    },
    {
      title: 'Total Vendido',
      dataIndex: 'totalAmount',
      width: 160,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table" style={{ fontWeight: 600 }}>{textNumberFormater(text)}</span>,
      sorter: (a: any, b: any) => a.totalAmount - b.totalAmount,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Impuestos',
      dataIndex: 'totalTaxes',
      width: 130,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>
    },
    {
      title: 'Descuentos',
      dataIndex: 'totalDiscounts',
      width: 130,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>
    },
    {
      title: '% Participación',
      dataIndex: 'percentage',
      width: 130,
      align: 'center',
      render: (text) => `${text}%`,
      sorter: (a: any, b: any) => a.percentage - b.percentage
    }
  ]

  const tableData = grouped.map((el, i) => ({
    ...el,
    rank: i + 1,
    key: el.key
  }))

  return (
    <div>
      <ReportSummaryCards cards={cards} loading={loading} />
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 800 }}
        size="small"
        loading={loading}
        summary={() => {
          if (tableData.length === 0) return null
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={2}>TOTALES</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="center">{summary.totalSales}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span className="numeric-values-data-table" style={{ fontWeight: 700, color: '#52c41a' }}>
                    {textNumberFormater(summary.totalAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span className="numeric-values-data-table">{textNumberFormater(summary.totalTaxes)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <span className="numeric-values-data-table">{textNumberFormater(summary.totalDiscounts)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="center">100%</Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )
        }}
      />
    </div>
  )
}

export const posExcelColumns: TExcelColumn[] = [
  { header: '#', key: 'rank', width: 6 },
  { header: 'Punto de Venta', key: 'label', width: 25 },
  { header: 'Facturas', key: 'count', width: 12, format: 'number' },
  { header: 'Total Vendido', key: 'totalAmount', width: 18, format: 'money' },
  { header: 'Impuestos', key: 'totalTaxes', width: 15, format: 'money' },
  { header: 'Descuentos', key: 'totalDiscounts', width: 15, format: 'money' },
  { header: '% Participación', key: 'percentage', width: 15, format: 'percent' }
]

export function getPosExcelData(data: IMovementRS[]): (TGroupedData & { rank: number })[] {
  return groupSalesByPos(data).map((el, i) => ({
    ...el,
    rank: i + 1
  }))
}

export default SalesByPosReport
